import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Link2, ArrowRight, ShieldCheck, Flame } from 'lucide-react';
import { API } from '../lib/constants';

const CoachPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [coach, setCoach] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | ready | notfound
  const [hudl, setHudl] = useState('');

  useEffect(() => {
    let active = true;
    axios.get(`${API}/api/coach/public/${slug}`)
      .then(res => { if (active) { setCoach(res.data.coach); setStatus('ready'); } })
      .catch(() => { if (active) setStatus('notfound'); });
    return () => { active = false; };
  }, [slug]);

  const submit = () => {
    const url = hudl.trim();
    if (!url.toLowerCase().includes('hudl.com')) { return; }
    navigate(`/create-profile?hudl=${encodeURIComponent(url)}&coach=${encodeURIComponent(slug)}`);
  };

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center" data-testid="coach-page-loading">
        <Loader2 className="h-8 w-8 animate-spin text-[#16A34A]" />
      </div>
    );
  }

  if (status === 'notfound') {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center px-4" data-testid="coach-page-notfound">
        <div className="text-center">
          <div className="flex items-center justify-center gap-2 mb-6">
            <img src="/offersz-o.png" alt="Offersz" className="w-9 h-9 rounded-full" />
            <span className="text-xl font-extrabold tracking-tight text-white">Offersz</span>
          </div>
          <h1 className="text-2xl font-black mb-2">This coach page doesn't exist</h1>
          <p className="text-white/50 mb-6">Double-check the link from your coach.</p>
          <button onClick={() => navigate('/')} data-testid="coach-notfound-home" className="bg-[#16A34A] text-white font-bold px-6 py-3 rounded-full">Go to Offersz</button>
        </div>
      </div>
    );
  }

  const initials = (coach.display_name || '?').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
  const handle = coach.handle ? coach.handle.replace(/^https?:\/\/(x|twitter)\.com\//i, '').replace(/^@/, '') : null;

  return (
    <div className="min-h-screen bg-black text-white" data-testid="coach-public-page">
      {/* Banner */}
      <div className="relative overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0F5132] via-[#0a0a0a] to-black" />
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-[#16A34A]/20 rounded-full blur-3xl" />
        <div className="relative max-w-3xl mx-auto px-5 pt-14 pb-10">
          <div className="flex items-center gap-2 mb-8">
            <img src="/offersz-o.png" alt="Offersz" className="w-8 h-8 rounded-full" />
            <span className="text-lg font-extrabold tracking-tight text-white/90">Offersz</span>
          </div>
          <div className="flex items-start gap-5">
            {coach.photo_url ? (
              <img src={coach.photo_url} alt={coach.display_name} data-testid="coach-photo" className="w-20 h-20 md:w-24 md:h-24 rounded-2xl object-cover border border-white/15 flex-shrink-0" />
            ) : (
              <div className="w-20 h-20 md:w-24 md:h-24 rounded-2xl bg-[#16A34A]/20 border border-[#16A34A]/30 flex items-center justify-center text-2xl font-black text-[#4ade80] flex-shrink-0">{initials}</div>
            )}
            <div className="min-w-0">
              <div className="inline-flex items-center gap-1.5 bg-[#16A34A]/15 text-[#4ade80] text-xs font-bold px-3 py-1 rounded-full mb-2">
                <ShieldCheck className="h-3.5 w-3.5" /> Recruiting with Offersz
              </div>
              <h1 className="text-3xl md:text-4xl font-black leading-tight" data-testid="coach-name">{coach.display_name}</h1>
              {coach.team_name && <p className="text-white/60 font-semibold mt-1" data-testid="coach-team">{coach.team_name}</p>}
              {handle && <a href={`https://x.com/${handle}`} target="_blank" rel="noreferrer" className="text-[#4ade80] text-sm font-semibold mt-1 inline-block">@{handle}</a>}
            </div>
          </div>
          {coach.bio && <p className="text-white/70 mt-5 leading-relaxed max-w-2xl" data-testid="coach-bio">{coach.bio}</p>}
          {coach.recruiting_area && (
            <div className="inline-flex items-center gap-2 mt-4 bg-white/[0.05] border border-white/10 rounded-full px-4 py-2 text-sm text-white/70" data-testid="coach-area">
              <Flame className="h-4 w-4 text-[#16A34A]" /> Recruiting: <span className="font-semibold text-white">{coach.recruiting_area}</span>
            </div>
          )}
        </div>
      </div>

      {/* Drop your film */}
      <div className="max-w-3xl mx-auto px-5 py-12">
        <div className="bg-white/[0.04] border border-white/10 rounded-3xl p-6 md:p-9">
          <h2 className="text-2xl font-black mb-2">Get on {(coach.display_name || 'Coach').replace(/^coach\s+/i, '').split(' ')[0]}'s board</h2>
          <p className="text-white/55 mb-6">Paste your Hudl link and we'll instantly build your Offersz recruiting profile — then send it straight to the coach.</p>
          <label className="text-sm font-semibold text-white/70 flex items-center gap-2 mb-2"><Link2 className="h-4 w-4" /> Your Hudl profile link</label>
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              value={hudl}
              onChange={e => setHudl(e.target.value)}
              onKeyPress={e => e.key === 'Enter' && submit()}
              placeholder="https://www.hudl.com/profile/..."
              data-testid="coach-hudl-input"
              className="flex-1 bg-white/5 border border-white/10 text-white h-13 min-h-[52px] rounded-xl px-4 outline-none focus:border-[#16A34A] transition-colors"
            />
            <button
              onClick={submit}
              disabled={!hudl.toLowerCase().includes('hudl.com')}
              data-testid="coach-submit-btn"
              className="bg-[#16A34A] hover:bg-[#0F5132] disabled:opacity-40 text-white font-bold px-6 min-h-[52px] rounded-xl flex items-center justify-center gap-2 transition-colors"
            >
              Send my film <ArrowRight className="h-5 w-5" />
            </button>
          </div>
          <p className="text-white/30 text-xs mt-4">No Hudl link?{' '}
            <button onClick={() => navigate(`/create-profile?coach=${encodeURIComponent(slug)}`)} data-testid="coach-manual-link" className="text-[#4ade80] font-semibold">Build it by hand</button>
          </p>
        </div>
        <p className="text-center text-white/25 text-xs mt-8">Powered by Offersz · the recruiting profile built for high-school football</p>
      </div>
    </div>
  );
};

export default CoachPage;
